<template>
  <section :class="['decoration-gallery', `mode-${mode}`]">
    <header class="decoration-heading">
      <div>
        <small>STARLIVE STYLE ATELIER</small>
        <h2>{{ $t(mode === "shop" ? "decoration.shopTitle" : "decoration.myTitle") }}</h2>
        <p>{{ $t(mode === "shop" ? "decoration.shopSubtitle" : "decoration.mySubtitle") }}</p>
      </div>
      <span v-if="mode === 'shop' && balance !== null"><i class="el-icon-coin" />{{ $n(balance) }}</span>
    </header>

    <nav class="decoration-types" :aria-label="$t('decoration.categoryLabel')">
      <button
        v-for="item in types"
        :key="item.code"
        :class="{ active: activeType === item.code }"
        type="button"
        @click="switchType(item.code)"
      >
        <i :class="item.icon" /><span>{{ $t(item.labelKey) }}</span>
      </button>
    </nav>

    <div v-if="mode === 'shop'" class="decoration-sort" :aria-label="$t('decoration.sortLabel')">
      <span>{{ $t("decoration.sortLabel") }}</span>
      <button
        v-for="option in sortOptions"
        :key="option.value"
        type="button"
        :class="{ active: activeCatalog.sort === option.value }"
        @click="changeSort(option.value)"
      >{{ $t(option.labelKey) }}</button>
    </div>

    <div v-if="initialLoading" class="decoration-state"><i class="el-icon-loading" />{{ $t("common.loading") }}</div>
    <div v-else-if="loadFailed && !items.length" class="decoration-state">
      <i class="el-icon-warning-outline" /><strong>{{ $t("decoration.loadFailed") }}</strong>
      <button type="button" @click="loadCatalog(true)">{{ $t("decoration.retry") }}</button>
    </div>
    <div v-else-if="!items.length" :class="['decoration-state', { 'empty-frame-invitation': mode === 'owned' && activeType === 'avatar_frame' }]">
      <template v-if="mode === 'owned' && activeType === 'avatar_frame'">
        <div class="empty-frame-avatar">
          <platform-avatar :src="resolvedUserAvatar" :alt="$t('decoration.avatarPreviewAlt')" @error="handleAvatarError" />
          <span><i class="el-icon-magic-stick" /></span>
        </div>
        <strong>{{ $t("decoration.emptyFrameTitle") }}</strong>
        <p>{{ $t("decoration.emptyFrameHint") }}</p>
        <button type="button" @click="$emit('browse-store', { type: 'avatar_frame' })">{{ $t("decoration.browseFrames") }} <i class="el-icon-arrow-right" /></button>
      </template>
      <template v-else>
        <i class="el-icon-magic-stick" /><strong>{{ $t("decoration.empty") }}</strong>
      </template>
    </div>
    <div v-else class="decoration-grid">
      <article
        v-for="item in items"
        :key="`${activeType}-${item.id}`"
        :class="{ equipped: isEquipped(item), owned: item.owned || mode === 'owned' }"
        role="button"
        tabindex="0"
        :aria-label="$t('decoration.previewItem', { name: item.name })"
        @click="openPreview(item)"
        @keyup.enter="openPreview(item)"
      >
        <div class="decoration-preview">
          <template v-if="activeType === 'avatar_frame'">
            <avatar-with-frame class="decoration-card-avatar" :avatar="resolvedUserAvatar" :avatar-alt="$t('decoration.avatarPreviewAlt')" :frame-preview-url="item._thumbFailed ? '' : cardPreviewUrl(item)" :frame-alt="item.name" :frame-decorative="false" :play-frame="false" @frame-error="$set(item, '_thumbFailed', true)" />
          </template>
          <template v-else>
            <img v-if="cardPreviewUrl(item) && !item._thumbFailed" class="floating-image" :src="cardPreviewUrl(item)" :alt="item.name" loading="lazy" @error="$set(item, '_thumbFailed', true)">
            <div v-else :class="['floating-preview', `theme-${item.theme || 'starlight'}`]">
              <i class="el-icon-star-on" /><b>StarLive</b><span>{{ item.name }}</span>
            </div>
          </template>
          <em v-if="isEquipped(item)">{{ $t("decoration.inUse") }}</em>
          <em v-else-if="item.owned || mode === 'owned'">{{ $t("decoration.owned") }}</em>
          <span class="preview-affordance"><i class="el-icon-view" />{{ $t("decoration.preview") }}</span>
        </div>
        <h3>{{ item.name }}</h3>
        <p>{{ item.description }}</p>
        <button
          v-if="mode === 'shop' && !item.owned"
          type="button"
          :disabled="busyId === item.id"
          @click.stop="purchase(item)"
        >
          <i class="el-icon-coin" />{{ $n(item.coin_price) }} {{ $t("wallet.coins") }}
        </button>
        <button v-else type="button" :class="{ secondary: isEquipped(item) }" :disabled="busyId === item.id" @click.stop="toggleEquip(item)">
          {{ $t(isEquipped(item) ? "decoration.remove" : "decoration.equip") }}
        </button>
      </article>
    </div>

    <footer v-if="mode === 'shop' && items.length" class="decoration-footer">
      <button v-if="hasMore" type="button" :disabled="loadingMore" @click="loadCatalog(false)">
        <i v-if="loadingMore" class="el-icon-loading" />{{ $t(loadingMore ? "decoration.loadingMore" : "decoration.loadMore") }}
      </button>
      <span v-else>{{ $t("decoration.allLoaded", { count: items.length }) }}</span>
      <button v-if="loadFailed && items.length" class="retry-link" type="button" @click="loadCatalog(false)">{{ $t("decoration.retry") }}</button>
    </footer>

    <el-dialog
      v-if="previewItem"
      :visible="true"
      append-to-body
      custom-class="decoration-preview-dialog"
      :show-close="false"
      :close-on-click-modal="true"
      :close-on-press-escape="true"
      width="calc(100% - 40px)"
      @close="closePreview"
    >
      <section class="preview-sheet" role="dialog" aria-modal="true" :aria-label="$t('decoration.previewTitle')">
          <header>
            <div><small>STARLIVE PREVIEW</small><h3>{{ previewItem.name }}</h3></div>
            <button type="button" :aria-label="$t('decoration.closePreview')" @click="closePreview"><i class="el-icon-close" /></button>
          </header>
          <div class="preview-stage">
            <template v-if="activeType === 'avatar_frame'">
              <avatar-with-frame class="sheet-avatar-pair" :avatar="resolvedUserAvatar" :avatar-alt="$t('decoration.avatarPreviewAlt')" :frame-animation-url="previewItem.image_url || ''" :frame-preview-url="previewItem.preview_url || ''" :frame-alt="previewItem.name" :frame-decorative="false" />
            </template>
            <template v-else>
              <img v-if="cardPreviewUrl(previewItem)" class="sheet-floating-image" :src="cardPreviewUrl(previewItem)" :alt="previewItem.name">
              <div v-else :class="['floating-preview', 'sheet-floating', `theme-${previewItem.theme || 'starlight'}`]">
                <i class="el-icon-star-on" /><b>StarLive</b><span>{{ previewItem.name }}</span>
              </div>
            </template>
          </div>
          <p>{{ previewItem.description || $t("decoration.previewHint") }}</p>
          <strong v-if="mode === 'shop'"><i class="el-icon-coin" />{{ $n(previewItem.coin_price) }} {{ $t("wallet.coins") }}</strong>
      </section>
    </el-dialog>
  </section>
</template>

<script>
import { equipDecoration, getDecorationCatalog, getOwnedDecorations, purchaseDecoration } from "../api/decoration"
import AvatarWithFrame from "./AvatarWithFrame.vue"

const DEFAULT_AVATAR = "https://starlivecdn.xyhy.live/public/media/avatars/default-avatar.svg"
const createCatalogState = () => ({
  items: [], page: 0, total: 0, hasMore: true, loaded: false,
  loading: false, loadingMore: false, sort: "recommended", requestSerial: 0
})

export default {
  name: "DecorationGallery",
  components: { AvatarWithFrame },
  props: {
    mode: { type: String, default: "shop" },
    loggedIn: { type: Boolean, default: false },
    userAvatar: { type: String, default: "" }
  },
  data() {
    return {
      activeType: "avatar_frame",
      busyId: 0,
      balance: null,
      loadFailed: false,
      avatarFailed: false,
      previewItem: null,
      catalog: { avatar_frame: createCatalogState(), floating_screen: createCatalogState() },
      owned: { avatar_frame: [], floating_screen: [] },
      ownedLoading: false,
      equipped: { avatar_frame_id: 0, floating_screen_id: 0 },
      types: [
        { code: "avatar_frame", icon: "el-icon-picture-outline-round", labelKey: "decoration.avatarFrames" },
        { code: "floating_screen", icon: "el-icon-magic-stick", labelKey: "decoration.floatingScreens" }
      ],
      sortOptions: [
        { value: "recommended", labelKey: "decoration.sortRecommended" },
        { value: "price_asc", labelKey: "decoration.priceLow" },
        { value: "price_desc", labelKey: "decoration.priceHigh" }
      ]
    }
  },
  computed: {
    activeCatalog() { return this.catalog[this.activeType] },
    items() { return this.mode === "shop" ? this.activeCatalog.items : this.owned[this.activeType] },
    initialLoading() { return this.mode === "shop" ? this.activeCatalog.loading : this.ownedLoading },
    loadingMore() { return this.mode === "shop" && this.activeCatalog.loadingMore },
    hasMore() { return this.mode === "shop" && this.activeCatalog.hasMore },
    resolvedUserAvatar() { return !this.avatarFailed && this.userAvatar ? this.userAvatar : DEFAULT_AVATAR }
  },
  watch: {
    loggedIn() { this.load(true) },
    userAvatar() { this.avatarFailed = false }
  },
  created() { this.load() },
  methods: {
    load(force = false) {
      if (this.mode === "owned") return this.loadOwned()
      return this.loadCatalog(force)
    },
    async loadOwned() {
      if (!this.loggedIn) {
        this.owned.avatar_frame = []
        this.owned.floating_screen = []
        return
      }
      this.ownedLoading = true
      try {
        const res = await getOwnedDecorations()
        this.owned.avatar_frame = res.data.avatar_frames || []
        this.owned.floating_screen = res.data.floating_screens || []
        this.equipped = res.data.equipped || this.equipped
      } finally {
        this.ownedLoading = false
      }
    },
    async loadCatalog(reset = false) {
      const state = this.activeCatalog
      if ((state.loading || state.loadingMore) || (!reset && state.loaded && !state.hasMore)) return
      if (reset) {
        state.items = []
        state.page = 0
        state.total = 0
        state.hasMore = true
        state.loaded = false
      }
      const requestType = this.activeType
      const requestSerial = ++state.requestSerial
      const page = state.page + 1
      this.loadFailed = false
      state.loading = page === 1
      state.loadingMore = page > 1
      try {
        const res = await getDecorationCatalog({ type: requestType, page, limit: 12, sort: state.sort })
        if (requestSerial !== state.requestSerial) return
        const list = res.data.list || []
        const known = new Set(state.items.map(item => Number(item.id)))
        state.items = page === 1 ? list : state.items.concat(list.filter(item => !known.has(Number(item.id))))
        state.page = page
        state.total = Number((res.data.pagination || {}).total || state.items.length)
        state.hasMore = Boolean((res.data.pagination || {}).has_more)
        state.loaded = true
        if (res.data.wallet_balance !== null && res.data.wallet_balance !== undefined) this.balance = Number(res.data.wallet_balance)
      } catch (error) {
        if (requestSerial === state.requestSerial) this.loadFailed = true
      } finally {
        if (requestSerial === state.requestSerial) {
          state.loading = false
          state.loadingMore = false
        }
      }
    },
    switchType(type) {
      if (this.activeType === type) return
      this.activeType = type
      this.closePreview()
      this.loadFailed = false
      if (this.mode === "shop" && !this.activeCatalog.loaded) this.loadCatalog(false)
    },
    changeSort(sort) {
      if (this.activeCatalog.sort === sort) return
      this.activeCatalog.requestSerial += 1
      this.activeCatalog.loading = false
      this.activeCatalog.loadingMore = false
      this.activeCatalog.sort = sort
      this.closePreview()
      this.loadCatalog(true)
    },
    cardPreviewUrl(item) {
      if (!item) return ""
      if (item.preview_url) return item.preview_url
      if (this.activeType === "avatar_frame" && item.media_format === "static") return item.image_url || ""
      return ""
    },
    openPreview(item) {
      if (!item) return
      this.previewItem = item
    },
    closePreview() {
      if (!this.previewItem) return
      this.previewItem = null
    },
    handleAvatarError() { this.avatarFailed = true },
    isEquipped(item) { return Number(this.equipped[`${this.activeType}_id`] || 0) === Number(item.id) },
    ensureLogin() {
      if (this.loggedIn) return true
      this.$emit("login")
      return false
    },
    async purchase(item) {
      if (!this.ensureLogin()) return
      try {
        await this.$notice.confirm(
          this.$t("decoration.purchaseConfirm", { name: item.name, price: this.$n(item.coin_price) }),
          this.$t("decoration.purchaseTitle"),
          { type: "warning", confirmButtonText: this.$t("decoration.buyNow") }
        )
      } catch (error) { return }
      this.busyId = item.id
      try {
        const res = await purchaseDecoration({
          type: this.activeType,
          id: item.id,
          client_request_no: `dec_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
        })
        this.$set(item, "owned", true)
        this.balance = Number(res.data.balance)
        this.$notice.success(this.$t("decoration.purchaseSuccess", { name: item.name }))
        this.$emit("changed")
      } finally { this.busyId = 0 }
    },
    async toggleEquip(item) {
      if (!this.ensureLogin()) return
      this.busyId = item.id
      try {
        const id = this.isEquipped(item) ? 0 : Number(item.id)
        await equipDecoration({ type: this.activeType, id })
        this.equipped[`${this.activeType}_id`] = id
        this.$notice.success(this.$t(id ? "decoration.equipSuccess" : "decoration.removeSuccess"))
        this.$emit("changed", { type: this.activeType, id, item })
      } finally { this.busyId = 0 }
    }
  }
}
</script>

<style scoped>
.decoration-gallery{padding:18px 16px 34px;color:#f8f5ef}.decoration-heading{display:flex;align-items:flex-start;justify-content:space-between;gap:14px;margin-bottom:16px}.decoration-heading small,.preview-sheet small{color:#d9b46f;font-size:9px;letter-spacing:2.1px}.decoration-heading h2{margin:5px 0 4px;font-family:Georgia,serif;font-size:24px}.decoration-heading p{margin:0;color:#8f9199;font-size:12px;line-height:1.55}.decoration-heading>span{display:flex;align-items:center;gap:5px;flex:none;padding:8px 11px;border:1px solid rgba(220,180,105,.28);border-radius:18px;background:#1c1a17;color:#efd29a;font-weight:700}.decoration-types{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:9px;margin-bottom:12px}.decoration-types button{display:flex;align-items:center;justify-content:center;gap:7px;padding:11px;border:1px solid #2d2e33;border-radius:14px;background:#17181c;color:#898b91}.decoration-types button.active{border-color:rgba(230,189,118,.55);background:linear-gradient(135deg,#2c251c,#1a1918);color:#f1d6a3}.decoration-sort{display:flex;align-items:center;gap:7px;margin:0 0 15px;overflow-x:auto;scrollbar-width:none}.decoration-sort>span{flex:none;color:#74767d;font-size:10px}.decoration-sort button{flex:none;padding:7px 10px;border:1px solid #292a30;border-radius:999px;background:#15161a;color:#8d8f96;font-size:10px}.decoration-sort button.active{border-color:#96733c;background:#2a2319;color:#f0ce91}.decoration-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}.decoration-grid article{min-width:0;padding:10px;border:1px solid #292a30;border-radius:18px;background:linear-gradient(160deg,#1a1b20,#121316);box-shadow:0 12px 30px rgba(0,0,0,.18);cursor:pointer}.decoration-grid article:focus-visible{outline:2px solid #d9b46f;outline-offset:2px}.decoration-grid article.equipped{border-color:rgba(230,189,118,.65)}.decoration-preview{position:relative;display:grid;place-items:center;height:142px;overflow:hidden;border-radius:14px;background:radial-gradient(circle at 50% 42%,#34313a,#17181d 68%)}.preview-avatar{width:76px;height:76px;border-radius:50%;object-fit:cover;background:#202126}.decoration-card-frame{position:absolute;width:116px;height:116px;object-fit:contain}.floating-image{width:100%;height:100%;object-fit:contain}.decoration-preview>em{position:absolute;inset-block-start:8px;inset-inline-end:8px;padding:4px 7px;border-radius:9px;background:rgba(12,13,15,.78);color:#e9c987;font-size:9px;font-style:normal}.preview-affordance{position:absolute;inset-inline-start:8px;inset-block-end:8px;padding:4px 7px;border-radius:10px;background:rgba(10,11,14,.7);color:#d8d9dd;font-size:9px}.floating-preview{position:relative;display:flex;align-items:center;gap:7px;width:90%;padding:13px;border-radius:12px;overflow:hidden;color:#fff}.floating-preview i,.floating-preview b,.floating-preview span{position:relative;z-index:1}.floating-preview span{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:10px}.theme-galaxy{background:linear-gradient(120deg,#3d267d,#ba3f79,#334b92)}.theme-gold{background:linear-gradient(120deg,#8a5c14,#d0a13a,#5b3610)}.theme-neon{background:linear-gradient(120deg,#10224f,#b4217d,#153d42)}.theme-rose{background:linear-gradient(120deg,#703249,#cb6682,#4a2637)}.decoration-grid h3{margin:10px 2px 3px;overflow:hidden;color:#f4f0e8;font-size:14px;text-overflow:ellipsis;white-space:nowrap}.decoration-grid p{height:32px;margin:0 2px 9px;overflow:hidden;color:#7f8189;font-size:10px;line-height:1.55}.decoration-grid article>button,.decoration-state button,.decoration-footer>button:first-child{width:100%;padding:9px 5px;border:0;border-radius:11px;background:linear-gradient(135deg,#d6ad64,#9b7233);color:#17130d;font-weight:800}.decoration-grid article>button.secondary{background:#25262b;color:#9b9da4}.decoration-grid button:disabled,.decoration-footer button:disabled{opacity:.55}.decoration-state{display:grid;place-items:center;gap:9px;min-height:220px;color:#85878f}.decoration-state>i{font-size:24px}.decoration-state button{width:auto;padding-inline:22px}.empty-frame-invitation{min-height:250px;padding:24px 18px;border:1px solid rgba(220,180,105,.2);border-radius:22px;background:radial-gradient(circle at 50% 28%,rgba(220,180,105,.13),transparent 42%),linear-gradient(160deg,#1a1b20,#121316);text-align:center}.empty-frame-avatar{position:relative;width:94px;height:94px}.empty-frame-avatar>img{width:74px;height:74px;margin:10px;border-radius:50%;object-fit:cover;box-shadow:0 0 0 1px rgba(255,255,255,.12),0 12px 26px rgba(0,0,0,.35)}.empty-frame-avatar>span{position:absolute;inset:0;border:1px dashed rgba(231,194,128,.55);border-radius:50%;animation:empty-frame-orbit 10s linear infinite}.empty-frame-avatar>span i{position:absolute;inset-block-start:-8px;inset-inline-start:38px;display:grid;place-items:center;width:20px;height:20px;border-radius:50%;background:#d9b46f;color:#17130d;font-size:11px;box-shadow:0 0 18px rgba(217,180,111,.5)}.empty-frame-invitation>strong{color:#f2eadb;font-family:Georgia,"Times New Roman",serif;font-size:18px}.empty-frame-invitation>p{max-width:260px;margin:0;color:#8f9199;font-size:11px;line-height:1.7}.empty-frame-invitation>button{display:flex;align-items:center;justify-content:center;gap:7px;min-width:170px;margin-top:5px}.decoration-footer{display:grid;gap:8px;place-items:center;padding:18px 4px 2px;color:#74767d;font-size:11px}.decoration-footer>button:first-child{max-width:220px}.decoration-footer .retry-link{border:0;background:transparent;color:#d6ad64}.preview-sheet{width:100%;max-height:min(74dvh,640px);padding:18px;overflow:auto;border:1px solid #303137;border-radius:24px;background:linear-gradient(170deg,#202126,#111216 64%);box-shadow:0 24px 70px rgba(0,0,0,.62)}.preview-sheet header{display:flex;align-items:center;justify-content:space-between}.preview-sheet header h3{margin:4px 0 0;font-family:Georgia,serif;font-size:21px}.preview-sheet header button{display:grid;place-items:center;width:36px;height:36px;border:1px solid #383940;border-radius:50%;background:#24252a;color:#ddd}.preview-stage{position:relative;display:grid;place-items:center;height:min(280px,42dvh);margin:16px 0;border:1px solid rgba(220,180,105,.14);border-radius:20px;background:radial-gradient(circle,#3a3541,#17181d 66%);overflow:hidden}.sheet-avatar{width:142px;height:142px;border-radius:50%;object-fit:cover;background:#202126}.sheet-frame{position:absolute;width:218px;height:218px;object-fit:contain}.sheet-floating-image{width:92%;height:86%;object-fit:contain}.sheet-floating{width:88%;padding:20px;font-size:18px}.preview-sheet>p{margin:0 0 12px;color:#92949b;font-size:12px;line-height:1.6}.preview-sheet>strong{display:flex;align-items:center;gap:5px;color:#efcb88}.mode-owned{padding:0}.mode-owned .decoration-heading{margin-top:4px}@keyframes empty-frame-orbit{to{transform:rotate(360deg)}}@media(max-width:340px){.decoration-gallery{padding-inline:12px}.decoration-grid{gap:8px}.decoration-preview{height:126px}.decoration-grid article{padding:8px}}@media(prefers-reduced-motion:reduce){.empty-frame-avatar>span{animation:none}}
.decoration-card-avatar{width:76px;height:76px}
.sheet-avatar-pair{width:142px;height:142px}
</style>

<style>
.decoration-preview-dialog.el-dialog{position:fixed;top:50%;left:50%;width:calc(100% - 40px)!important;max-width:360px;margin:0!important;overflow:hidden;border:0;border-radius:24px;background:transparent;box-shadow:none;transform:translate(-50%,-50%)!important}
.decoration-preview-dialog .el-dialog__header{display:none}
.decoration-preview-dialog .el-dialog__body{padding:0;color:inherit}
.dialog-fade-enter-active .decoration-preview-dialog.el-dialog,.dialog-fade-leave-active .decoration-preview-dialog.el-dialog{animation:none!important}
</style>
